import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  LeadDetail,
  ListLeadsQuery,
  Region,
  UpdateLeadStatusBody,
} from '@auctionradar/shared';
import { api } from './client';

/**
 * Query-key conventions:
 *   ['leads', 'list', queryObject]   → list endpoint
 *   ['leads', 'detail', id]           → single lead endpoint
 *
 * The nested array shape lets us invalidate broadly (`['leads']`) when
 * a status update mutation succeeds, without hard-coding every variant.
 */
export const queryKeys = {
  leads: {
    all: ['leads'] as const,
    list: (q: ListLeadsQuery) => ['leads', 'list', q] as const,
    detail: (id: string) => ['leads', 'detail', id] as const,
  },
};

export function useLeads(q: ListLeadsQuery = {}) {
  return useQuery({
    queryKey: queryKeys.leads.list(q),
    queryFn: () => api.listLeads(q),
    // Keep showing the previous page while the next one loads — avoids
    // empty-state flash when the user changes filters or pages.
    placeholderData: (prev) => prev,
  });
}

export function useLead(id: string | undefined) {
  return useQuery({
    queryKey: id ? queryKeys.leads.detail(id) : ['leads', 'detail', 'pending'],
    queryFn: () => api.getLead(id as string),
    enabled: id != null,
  });
}

export function useLeadMapPins() {
  return useQuery({
    queryKey: ['leadMapPins'] as const,
    queryFn: () => api.leadMapPins(),
    staleTime: 60 * 1000,
  });
}

export function useMarketPrices(region?: Region) {
  return useQuery({
    queryKey: ['marketPrices', region ?? 'all'] as const,
    queryFn: () => api.listMarketPrices(region),
    // Pricemap recomputes weekly — long staleTime is safe.
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Per-zone price rows (one row per sqm × year_band combo). Used by the
 * Atlas detail panel. `slug` may be null when nothing is selected; the
 * query disables itself in that case.
 */
export function useZonePrices(slug: string | null) {
  return useQuery({
    queryKey: ['zonePrices', slug ?? 'none'] as const,
    queryFn: () => api.zonePrices(slug as string),
    enabled: slug != null,
    staleTime: 5 * 60 * 1000,
  });
}

export function useFunnel() {
  return useQuery({
    queryKey: ['analytics', 'funnel'] as const,
    queryFn: () => api.funnel(),
  });
}

export function useByMunicipality() {
  return useQuery({
    queryKey: ['analytics', 'byMunicipality'] as const,
    queryFn: () => api.byMunicipality(),
  });
}

/**
 * Manual status mutation for the dashboard. On success:
 *   - Seeds the new LeadDetail straight into the detail cache so the page
 *     re-renders without a network roundtrip.
 *   - Invalidates the list cache so the Feed re-fetches with the new status
 *     visible (so a freshly-dismissed lead drops out of the default view).
 */
export function useUpdateLeadStatus(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateLeadStatusBody) => api.updateLeadStatus(id, body),
    onSuccess: (next: LeadDetail) => {
      qc.setQueryData(queryKeys.leads.detail(id), next);
      qc.invalidateQueries({ queryKey: queryKeys.leads.all });
    },
  });
}
